// src/features/system/admin/components/ServiceManagement.tsx
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import MinimalisticTable, { ColumnDef }
  from '../../../../shared/components/ui/MinimalisticTable';
import ExpandableTable from '../../../../shared/components/ui/ExpandableTable';
import { fetchServicesApi, Service, Script } from '../../services/api/servicesApi';
import TenantsTable from '@/features/system/tenants/components/TenantsTable';
import CreateServiceDialog from './CreateServiceDialog';
import CreateScriptDialog from './CreateScriptDialog';

/** Script row for the table – we inject `service_name` during flattening */
type ScriptRow = Script & { service_name: string };

export default function ServiceManagement() {
  const [openCreate, setOpenCreate] = useState(false);
  const [openCreateScript, setOpenCreateScript] = useState(false);
  const [scriptSvc, setScriptSvc] = useState<Service | null>(null);
  /* ─── load services ───────────────────────────── */
  const { data: services = [], isLoading, error } = useQuery<Service[], Error>({
    queryKey: ['admin', 'services'],
    queryFn : fetchServicesApi,
  });

  /* ─── flatten scripts ─────────────────────────── */
  const scripts: ScriptRow[] = useMemo(
    () =>
      services.flatMap((svc: Service) =>
        (svc.scripts ?? []).map((scr: Script) => ({
          ...scr,
          service_name: svc.service_name,   // enrich for table
        })),
      ),
    [services]
  );

  /* ─── column definitions ──────────────────────── */
  const serviceCols: ColumnDef<Service>[] = [
    { header: 'ID',        accessor: 'id_service'  },
    { header: 'Name',      accessor: 'service_name'},
    { header: 'Host',      accessor: 'service_host', Cell: (val, row) => val || '—' },
    { header: 'Port',      accessor: 'service_port', Cell: (val) => (val ?? '—') },
    { header: 'DB Table',  accessor: 'service_table', Cell: (val) => val || '—' },
  ];

  const scriptCols: ColumnDef<ScriptRow>[] = [
    { header: 'Service', accessor: 'service_name' },
    { header: 'ID',      accessor: 'id_script'    },
    { header: 'Script',  accessor: 'script_name'  },
  ];

  /* ─── render ──────────────────────────────────── */
  return (
    <section className="space-y-8">
      {/* Tenants moved here from System Management */}
      <div className="bg-neutral-900 p-4 rounded-md">
        <h3 className="mb-2 font-semibold">Tenants</h3>
        <TenantsTable />
      </div>
      {/* Services expandable table (scripts inside expansion) */}
      <div className="bg-neutral-900 p-4 rounded-md">
        <h3 className="mb-2 font-semibold">Services & Scripts</h3>
        {isLoading && <p className="text-sm text-neutral-400">Loading…</p>}
        {error && <p className="text-sm text-rose-400">Error: {error.message}</p>}
        {!isLoading && !error && (
          <ExpandableTable<Service>
            columns={serviceCols}
            data={services}
            rowKey={(s) => s.id_service}
            noDataMessage="No services found."
            showNewButton
            onNew={() => setOpenCreate(true)}
            showSearch
            searchPlaceholder="Filter services…"
            renderSubRow={(svc) => {
              const svcScripts = (svc.scripts || []).sort((a,b)=>a.id_script-b.id_script);
              if (!svcScripts.length) {
                return (
                  <div className="text-neutral-500 text-sm flex items-center justify-between">
                    <span>No scripts for this service.</span>
                    <button
                      onClick={() => { setScriptSvc(svc); setOpenCreateScript(true); }}
                      className="text-xs px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-500"
                    >New Script</button>
                  </div>
                );
              }
              return (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-neutral-400">
                      <th className="px-2 py-1 text-left">Script ID</th>
                      <th className="px-2 py-1 text-left">Name</th>
                      <th className="px-2 py-1 text-left">Table</th>
                      <th className="px-2 py-1 text-right">
                        <button
                          onClick={() => { setScriptSvc(svc); setOpenCreateScript(true); }}
                          className="text-xs px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-500"
                        >New</button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {svcScripts.map(scr => (
                      <tr key={scr.id_script} className="border-t border-neutral-800">
                        <td className="px-2 py-1 text-neutral-300">{scr.id_script}</td>
                        <td className="px-2 py-1 text-neutral-100">{scr.script_name || '—'}</td>
                        <td className="px-2 py-1 text-neutral-400">{(scr as any).table_name || '—'}</td>
                        <td className="px-2 py-1 text-right text-neutral-500">{/* actions placeholder */}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            }}
          />
        )}
      </div>
      <CreateServiceDialog open={openCreate} onOpenChange={setOpenCreate} />
      <CreateScriptDialog
        open={openCreateScript}
        onOpenChange={(v)=>{ if(!v) setScriptSvc(null); setOpenCreateScript(v); }}
        service={scriptSvc ? { id_service: scriptSvc.id_service, service_name: scriptSvc.service_name } : null}
      />
    </section>
  );
}
